import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, StopCircle, PlayCircle, RotateCcw, CheckCircle, Trophy, Mail, Loader2, Copy } from 'lucide-react';
import {
  createSeason,
  startSeason,
  closeSeason,
  resetSeason,
  sendEndOfSeasonNewsletter,
  copyRacesFromSeason,
} from '../../services/api';

const SeasonsTab = ({ seasons, refreshSeasons, toast }) => {
  // Local form states
  const [newSeasonYear, setNewSeasonYear] = useState(2027);
  const [closeSeasonYear, setCloseSeasonYear] = useState('');
  const [closingSeasonLoading, setClosingSeasonLoading] = useState(false);
  const [newsletterSeasonYear, setNewsletterSeasonYear] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  // Copy races states
  const [copyTargetYear, setCopyTargetYear] = useState('');
  const [copySourceYear, setCopySourceYear] = useState('');
  const [copyingRaces, setCopyingRaces] = useState(false);

  const handleCreateSeason = async () => {
    try {
      await createSeason(newSeasonYear);
      if (toast) toast({
        title: 'Seizoen aangemaakt',
        description: `Seizoen ${newSeasonYear} is aangemaakt`,
      });
      if (refreshSeasons) await refreshSeasons();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet aanmaken',
        variant: 'destructive',
      });
    }
  };

  const handleStartSeason = async (year) => {
    try {
      await startSeason(year);
      if (toast) toast({
        title: 'Seizoen gestart',
        description: `Seizoen ${year} is nu actief`,
      });
      if (refreshSeasons) await refreshSeasons();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet starten',
        variant: 'destructive',
      });
    }
  };

  const handleCloseSeason = async () => {
    if (!closeSeasonYear) {
      if (toast) toast({
        title: 'Selecteer een seizoen',
        description: 'Kies het seizoen dat je wilt afsluiten',
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Weet je zeker dat je seizoen ${closeSeasonYear} wilt afsluiten? Dit archiveert alle data en kan niet ongedaan worden gemaakt.`)) {
      return;
    }

    setClosingSeasonLoading(true);
    try {
      await closeSeason(parseInt(closeSeasonYear));
      if (toast) toast({
        title: 'Seizoen afgesloten',
        description: `Seizoen ${closeSeasonYear} is gearchiveerd`,
      });
      setCloseSeasonYear('');
      if (refreshSeasons) await refreshSeasons();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet afsluiten',
        variant: 'destructive',
      });
    } finally {
      setClosingSeasonLoading(false);
    }
  };

  const handleResetSeason = async (year) => {
    const confirmMessage = `Weet je zeker dat je seizoen ${year} wilt resetten?\n\n` +
      `Dit verwijdert:\n` +
      `• Alle voorspellingen van gebruikers\n` +
      `• Alle race resultaten\n` +
      `• Alle kampioenschapsresultaten\n` +
      `• Alle punten worden op 0 gezet\n\n` +
      `De races met hun datums en tijden blijven behouden.\n` +
      `Het seizoen wordt weer actief.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      await resetSeason(year);
      if (toast) toast({
        title: 'Seizoen gereset',
        description: `Seizoen ${year} is gereset en weer actief. Alle voorspellingen en resultaten zijn verwijderd.`,
      });
      if (refreshSeasons) await refreshSeasons();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon seizoen niet resetten',
        variant: 'destructive',
      });
    }
  };

  const handleSendEndOfSeasonNewsletter = async () => {
    if (!newsletterSeasonYear) {
      if (toast) toast({
        title: 'Selecteer een seizoen',
        description: 'Kies een seizoen om de einde seizoen nieuwsbrief te versturen',
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Weet je zeker dat je de einde seizoen nieuwsbrief wilt versturen voor ${newsletterSeasonYear}? Dit stuurt een email naar alle actieve gebruikers.`)) {
      return;
    }

    setSendingNewsletter(true);
    try {
      const result = await sendEndOfSeasonNewsletter(parseInt(newsletterSeasonYear));
      if (toast) toast({
        title: 'Nieuwsbrief verstuurd',
        description: `Einde seizoen nieuwsbrief verstuurd naar ${result.emails_sent || 0} gebruikers`,
      });
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon nieuwsbrief niet versturen',
        variant: 'destructive',
      });
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleCopyRaces = async () => {
    if (!copyTargetYear || !copySourceYear) {
      if (toast) toast({
        title: 'Selecteer beide seizoenen',
        description: 'Kies een bron- en doelseizoen om races te kopiëren',
        variant: 'destructive',
      });
      return;
    }

    if (copyTargetYear === copySourceYear) {
      if (toast) toast({
        title: 'Ongeldige selectie',
        description: 'Bron- en doelseizoen mogen niet hetzelfde zijn',
        variant: 'destructive',
      });
      return;
    }

    if (!window.confirm(`Weet je zeker dat je de races van seizoen ${copySourceYear} wilt kopiëren naar seizoen ${copyTargetYear}? De datums worden automatisch met ${parseInt(copyTargetYear) - parseInt(copySourceYear)} jaar aangepast.`)) {
      return;
    }

    setCopyingRaces(true);
    try {
      const result = await copyRacesFromSeason(parseInt(copyTargetYear), parseInt(copySourceYear));
      if (toast) toast({
        title: 'Races gekopieerd',
        description: result.message,
      });
      setCopyTargetYear('');
      setCopySourceYear('');
      if (refreshSeasons) await refreshSeasons();
    } catch (error) {
      if (toast) toast({
        title: 'Fout',
        description: error.response?.data?.detail || 'Kon races niet kopiëren',
        variant: 'destructive',
      });
    } finally {
      setCopyingRaces(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Create Season */}
        <Card className="border-2 border-[#FDB913]">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Plus className="mr-2 text-[#FDB913]" size={20} />
              Nieuw Seizoen Aanmaken
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Seizoen Jaar</Label>
              <Input
                type="number"
                value={newSeasonYear}
                onChange={(e) => setNewSeasonYear(parseInt(e.target.value))}
                min={2024}
                max={2050}
              />
            </div>
            <Button 
              onClick={handleCreateSeason}
              className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911]"
            >
              <Plus className="mr-2" size={16} />
              Seizoen Aanmaken
            </Button>
          </CardContent>
        </Card>

        {/* Close Season */}
        <Card className="border-2 border-red-500">
          <CardHeader>
            <CardTitle className="flex items-center text-red-600">
              <StopCircle className="mr-2" size={20} />
              Seizoen Afsluiten
            </CardTitle>
            <CardDescription>
              Sluit een seizoen af en archiveer alle data. Kampioenen worden automatisch overgenomen uit de Resultaten.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Seizoen</Label>
              <Select value={closeSeasonYear} onValueChange={setCloseSeasonYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer seizoen" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.filter(s => !s.is_archived).map((season) => (
                    <SelectItem key={season.year} value={season.year.toString()}>
                      {season.year} {season.is_active && '(Actief)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              ⚠️ Zorg dat de WK en CC resultaten zijn ingevuld in het Resultaten tabblad voordat je het seizoen afsluit.
            </p>
            <Button 
              onClick={handleCloseSeason}
              variant="destructive"
              className="w-full"
              disabled={closingSeasonLoading || !closeSeasonYear}
            >
              {closingSeasonLoading ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Afsluiten...
                </>
              ) : (
                <>
                  <StopCircle className="mr-2" size={16} />
                  Seizoen Afsluiten
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* End of Season Newsletter */}
        <Card className="border-2 border-blue-500">
          <CardHeader>
            <CardTitle className="flex items-center text-blue-600 dark:text-blue-400">
              <Mail className="mr-2" size={20} />
              Einde Seizoen Nieuwsbrief
            </CardTitle>
            <CardDescription>
              Verstuur een nieuwsbrief met de eindstand en prijswinnaars
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Seizoen</Label>
              <Select value={newsletterSeasonYear} onValueChange={setNewsletterSeasonYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer seizoen" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.filter(s => s.is_archived || s.is_active).map((season) => (
                    <SelectItem key={season.year} value={season.year.toString()}>
                      {season.year} {season.is_active && '(Actief)'} {season.is_archived && '(Gearchiveerd)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button 
              onClick={handleSendEndOfSeasonNewsletter}
              disabled={sendingNewsletter || !newsletterSeasonYear}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-end-of-season-newsletter-btn"
            >
              {sendingNewsletter ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Versturen...
                </>
              ) : (
                <>
                  <Mail className="mr-2" size={16} />
                  Verstuur Nieuwsbrief
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Copy Races */}
        <Card className="border-2 border-purple-500">
          <CardHeader>
            <CardTitle className="flex items-center text-purple-600 dark:text-purple-400">
              <Copy className="mr-2" size={20} />
              Races Kopiëren
            </CardTitle>
            <CardDescription>
              Kopieer races van een vorig seizoen naar een nieuw seizoen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Van Seizoen (bron)</Label>
              <Select value={copySourceYear} onValueChange={setCopySourceYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer bronseizoen" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.map((season) => (
                    <SelectItem key={season.year} value={season.year.toString()}>
                      {season.year} {season.is_active && '(Actief)'} {season.is_archived && '(Gearchiveerd)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Naar Seizoen (doel)</Label>
              <Select value={copyTargetYear} onValueChange={setCopyTargetYear}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer doelseizoen" />
                </SelectTrigger>
                <SelectContent>
                  {seasons.filter(s => !s.is_archived).map((season) => (
                    <SelectItem key={season.year} value={season.year.toString()}>
                      {season.year} {season.is_active && '(Actief)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              💡 De datums worden automatisch aangepast naar het doelseizoen.
            </p>
            <Button 
              onClick={handleCopyRaces}
              disabled={copyingRaces || !copySourceYear || !copyTargetYear}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            >
              {copyingRaces ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} />
                  Kopiëren...
                </>
              ) : (
                <>
                  <Copy className="mr-2" size={16} />
                  Races Kopiëren
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Seasons List */}
      <Card>
        <CardHeader>
          <CardTitle>Alle Seizoenen</CardTitle>
        </CardHeader>
        <CardContent>
          {seasons.length === 0 ? (
            <p className="text-muted-foreground">Nog geen seizoenen aangemaakt</p>
          ) : (
            <div className="space-y-4">
              {seasons.map((season) => (
                <div key={season.year} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center space-x-4">
                    <span className="text-2xl font-bold">{season.year}</span>
                    {season.is_active && (
                      <span className="bg-green-500/20 dark:bg-green-900/50 text-green-800 dark:text-green-400 px-2 py-1 rounded text-sm flex items-center">
                        <CheckCircle size={14} className="mr-1" />
                        Actief
                      </span>
                    )}
                    {season.is_archived && (
                      <span className="bg-secondary text-foreground px-2 py-1 rounded text-sm">
                        Gearchiveerd
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    {!season.is_active && !season.is_archived && (
                      <Button 
                        onClick={() => handleStartSeason(season.year)}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        <PlayCircle className="mr-1" size={14} />
                        Starten
                      </Button>
                    )}
                    {/* Reset button - always available */}
                    <Button 
                      onClick={() => handleResetSeason(season.year)}
                      variant="outline"
                      size="sm"
                      className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950"
                    >
                      <RotateCcw className="mr-1" size={14} />
                      Reset
                    </Button>
                    {season.is_archived && season.final_standings && (
                      <div className="text-sm text-muted-foreground">
                        <Trophy className="inline mr-1" size={14} />
                        WK: {season.final_standings.world_champion}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SeasonsTab;
